import { useCallback, useEffect, useState } from "react"
import { useCore } from "./useCore"
import { useForm } from "./useForm"
import { useFunctions } from "./useFunctions"
import { useShopify } from "./useShopify"

import type { Location } from "@root/types/global"

type GiftcardBalance = {
  initial: number
  remaining: number
}

type GiftcardBalanceParsed = {
  initial: string
  remaining: string
}

type GiftcardCodeParsed = {
  full: string
  partial: string
}

type GiftcardCustomer = {
  email: string
  name: string
}

type Giftcard = {
  balance: GiftcardBalance
  code: string
  currency: string
  customer: GiftcardCustomer
  enabled: boolean
  expired: string
  expires: string
}

type GiftcardParsed = {
  balance: GiftcardBalanceParsed
  code: GiftcardCodeParsed
  currency: string
  customer: GiftcardCustomer
  enabled: boolean
  expired: string
  expires: string | null
}

export type UseGiftcard = {
  card: GiftcardParsed | null
  errors: Array<string>
  handleShow: (event: React.BaseSyntheticEvent) => void
  show: boolean
}

export const useGiftcard = (location: Location): UseGiftcard => {
  const {
    helpers: { decodeBase64 },
  } = useCore()
  const { formatMoney } = useShopify()

  const [card, setCard] = useState<GiftcardParsed | null>(null)
  const [errors, setErrors] = useState<Array<string>>([])
  const [show, setShow] = useState<boolean>(false)

  const getCard = useCallback(() => {
    const parts = location?.pathname?.split(`/`) || []
    const valid = parts?.length === 3
    const rawGiftcard = valid ? parts?.[parts?.length - 1] || null : null

    try {
      const rawCard: Giftcard | null = rawGiftcard ? JSON.parse(decodeBase64(rawGiftcard)) : null

      if (rawCard?.code) {
        const card = {
          ...rawCard,
          balance: {
            initial: formatMoney(rawCard?.balance?.initial),
            remaining: formatMoney(rawCard?.balance?.remaining),
          },
          code: {
            partial: rawCard?.code?.slice(-4)?.padStart(rawCard?.code?.length, "*"),
            full: rawCard?.code?.match(/.{1,4}/g)?.join(" ") || "",
          },
          enabled: rawCard?.enabled && !rawCard?.expired ? true : false,
          expires: rawCard?.expires
            ? new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(rawCard.expires))
            : null,
        }
        setCard(card)
      }
    } catch (error) {
      setErrors([(error as Error)?.message])
    }
  }, [decodeBase64, formatMoney, location?.pathname])

  const handleShow = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.preventDefault()

      setShow(active => !active)
    },
    [setShow]
  )

  // intentionally only run once at first render
  useEffect(() => {
    getCard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    card,
    errors,
    handleShow,
    show,
  }
}

export type GiftCardBalanceRespnseError = {
  code: number
  field: string
  message: string
}

export type GiftCardBalanceResponse = {
  available_amount?: number
  expiry_date?: Date
  code: number
  errors?: Array<GiftCardBalanceRespnseError>
  message: string
  original_amount?: number
  used_amount?: number
  status: string
  voucher_number?: string
  history?: Array<GiftCardTransactionsResponse>
}

export type GiftCardTransactionsResponse = {
  transaction_date: string
  transaction_type: string
  amount: number
  store_code: string
  transaction_reference: string
}

export type GiftCardTransactions = Omit<GiftCardTransactionsResponse, "amount"> & {
  amount: string
}

export type GiftCardBalance = Omit<GiftCardBalanceResponse, "errors"> & {
  amount?: string
  date?: string
  errors?: Array<string>
}

export type GiftCardData = {
  pin: string
  voucher: string
}

export type UseGiftcardBalance = {
  balance: GiftCardBalance | null
  data: GiftCardData | null
  transactions: Array<GiftCardTransactions> | null
  errors: Array<string>
  handleChange: (event: React.BaseSyntheticEvent) => void
  handleSubmit: (event: React.BaseSyntheticEvent) => void
  loading: boolean
}

export const useGiftcardBalance = (): UseGiftcardBalance => {
  const { data, handleChange } = useForm({ pin: "", voucher: "" })
  const { callFunction } = useFunctions()
  const { formatMoney } = useShopify()

  const [balance, setBalance] = useState<GiftCardBalance | null>(null)
  const [transactions, setTransactions] = useState<GiftCardTransactions[]>([])
  const [errors, setErrors] = useState<Array<string>>([])
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = useCallback(
    async (event: React.BaseSyntheticEvent) => {
      event.preventDefault()

      setBalance(null)
      setTransactions([])
      setErrors([])
      setLoading(true)

      const empty = Object.entries(data).filter(entry => !entry[1])

      if (empty?.length) {
        setErrors(empty.map(([key]) => key))
        setLoading(false)

        return
      }

      const { status, body } = await callFunction("check-balance", data)

      if (status === "success") {
        const balance = {
          ...body?.balance,
          amount: typeof body?.balance.available_amount !== "undefined" ? formatMoney(body?.balance.available_amount) : undefined,
          date: body?.balance.expiry_date
            ? new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "long", year: "numeric" }).format(
                new Date(body?.balance.expiry_date)
              )
            : undefined,
        }

        const history =
          body?.history?.history?.map(transaction => ({
            ...transaction,
            transaction_date: new Intl.DateTimeFormat("en-AU", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(transaction.transaction_date)),
            amount: `$${transaction.amount}`,
          })) || []

        setTransactions(history)
        setBalance(balance)
      } else {
        setErrors(["Gift card not found, please check your gift card details and try again."])
      }

      setLoading(false)
    },
    [callFunction, data, formatMoney, setBalance, setTransactions, setLoading]
  )

  return {
    balance,
    transactions,
    data,
    errors,
    handleChange,
    handleSubmit,
    loading,
  }
}
